import React, { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubPage, { PageAction, PageActions } from '#/common/components/SubPage';
import useAssetQuery from '../hooks/useAssetQuery';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import AssetForm from '../components/AssetForm';
import If from '#/common/components/If';
import { Findings } from '#/modules/findings';
import WorkOrders from '#/modules/workOrder/components/WorkOrders/WorkOrders';
import useAssetRelatedCounts from '../hooks/useAssetRelatedCountsQuery';
import getFormTitle from '#/common/utils/getFormTitle';
import { Asset } from '#/graphql/types';
import { states } from '../utils/mappings';

export interface AssetParams {
  readonly id?: string;
}
export type AssetTabs = 'details' | 'work-orders' | 'findings';

export const defaultTab: AssetTabs = 'details';

export interface AssetsProps {
  readonly tab?: AssetTabs;
}

const tabs: Tab<AssetTabs>[] = [
  {
    key: 'details',
    text: 'Details',
    type: 'form',
  },
  {
    key: 'work-orders',
    text: 'Work Orders',
    type: 'grid',
  },
  {
    key: 'findings',
    text: 'Findings',
    type: 'grid',
  },
];

const woOmitCols = ['asset.assetTag', 'asset'];
const findingsOmitCols = ['asset.assetTag'];

const AssetPage: React.FC<AssetsProps> = ({ tab }) => {
  const { id } = useParams() as AssetParams;
  // Check if we're creating a new asset
  const isNewAsset = id === 'new';

  const assetQuery = useAssetQuery(id, { skip: isNewAsset });
  const asset = assetQuery.data?.getAsset?.data as Asset;

  // Use a ref to store the initial updatedAt value to prevent form remount
  // on background refetches (cache-and-network).
  const formKeyRef = useRef<string | null>(null);
  if (asset && !formKeyRef.current) {
    formKeyRef.current = asset.updatedAt ?? asset.id ?? null;
  }

  const { data } = useAssetRelatedCounts(id ?? '');
  const tenantId = asset?.tenantId;

  const assetTag = asset?.assetTag;
  const title = getFormTitle(id, assetTag, 'Asset');

  const assetsTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'work-orders')
          return {
            ...t,
            count: data?.workOrders ?? 0,
          };
        if (t.key === 'findings')
          return {
            ...t,
            count: data?.findings ?? 0,
          };
        return t;
      }),
    [data, id, tenantId],
  );

  const actions = useMemo<PageAction[]>(
    () => [
      {
        id: `new-${tab === 'work-orders' ? 'work-order' : 'finding'}`,
        alwaysShow: true,
        type: 'button',
        props: {
          visible: asset?.state !== states.retired.key,
          type: 'submit',
          icon: 'add',
          label: `Create New ${tab === 'work-orders' ? 'Work Order' : 'Finding'}`,
          responsive: true,
          path: `/${tab === 'work-orders' ? 'work-orders' : 'findings'}/new${asset?.id ? `?assetId=${asset.id}` : ''}`,
        },
      },
    ],
    [asset?.id, tab, asset?.state],
  );

  return (
    <SubPage
      selectedTab={tab || 'details'}
      tabs={isNewAsset ? undefined : assetsTabs}
      title={title}
      isLoading={assetQuery.loading}
      showGoBackButton
      type={!tab || tab === 'details' ? 'form' : 'list'}>
      <If condition={tab === 'details' || tab === undefined}>
        {<AssetForm key={`asset-${id}-${formKeyRef.current}`} title={title} asset={asset} />}
      </If>
      <If condition={tab === 'work-orders' || tab === 'findings'}>
        <PageActions actions={actions} />
      </If>
      <If condition={tab === 'work-orders'}>
        {
          <WorkOrders
            omitColumns={woOmitCols}
            permanentFilters={id ? [['assetId', '=', id]] : undefined}
            showOnlyInternal
          />
        }
      </If>
      <If condition={tab === 'findings'}>
        {
          <Findings
            omitColumns={findingsOmitCols}
            permanentFilters={id ? [['assetId', '=', id]] : undefined}
          />
        }
      </If>
    </SubPage>
  );
};

export default React.memo(AssetPage);
